import { Navigate, RouteObject } from 'react-router-dom';
import { FormEvent, ReactNode, useState } from 'react';
import {
	AuthLayout,
	PublicSchedulingLayout,
	PublicSessionAccessLayout,
} from '@/layouts';
import {
	BlogListPage,
	BlogPostPage,
	CreatePublicSession,
	SessaoAcessoPublicoPage,
	CollaboratorOnboarding,
	Landing,
	NewPage,
	OauthCallback,
	Recover,
	RecoverCode,
	RecoverPassword,
	TutorialPage,
	MatchPage,
} from '@/pages';
import ProfessionalTermsOfUsePage from '@/pages/professional-terms-of-use';
import PrivacyPolicyPage from '@/pages/public/privacy-policy';
import TermsOfUsePage from '@/pages/terms-of-use';
import BenefitTermsOfUsePage from '@/pages/benefit-terms-of-use';
import { NotFound } from '@/pages/public/NotFound';
import { SandboxEnterPage } from '@/pages/public/sandbox/SandboxEnterPage';
// import { BenefitPage } from "@/pages/public/benefit";
import { PlanSelectionPage } from '@/pages/public/benefit/plans';
import { BusinessLanding } from '@/pages/public/business-landing/BusinessLanding';
import { PsychologistLandingPage } from '@/pages/public/psychologist-landing';
import { PropostaPublica } from '@/pages/public/proposta-publica/PropostaPublica';
import { PropostaGeralPage } from '@/pages/public/proposta-publica/PropostaGeralPage';
import { ApresentacaoPage } from '@/pages/public/apresentacao/ApresentacaoPage';
import { DemonstracaoDashboardPage } from '@/pages/public/demonstracao-dashboard/DemonstracaoDashboardPage';
import { DemonstracaoLaudoPage } from '@/pages/public/demonstracao-laudo/DemonstracaoLaudoPage';
import { DemonstracaoDevolutivaPage } from '@/pages/public/demonstracao-devolutiva/DemonstracaoDevolutivaPage';

const PROPOSTA_PASSWORD = 'dugodo2026.';
const PROPOSTA_GERAL_PASSWORD = 'F3v7s2';
/** Senha só para rotas de demonstração estática (painel, laudo e devolutiva, sem API). */
const DEMONSTRACAO_DASHBOARD_PASSWORD = 'plumaa-demo';

const ProtectedPasswordRoute = ({
	password,
	children,
}: {
	password: string;
	children: ReactNode;
}) => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [passwordInput, setPasswordInput] = useState('');
	const [hasError, setHasError] = useState(false);

	const handleSubmitPassword = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (passwordInput === password) {
			setIsAuthenticated(true);
			setHasError(false);
			return;
		}

		setHasError(true);
		setPasswordInput('');
	};

	if (!isAuthenticated) {
		return (
			<section className="flex min-h-screen items-center justify-center bg-zinc-950 px-6">
				<form
					onSubmit={handleSubmitPassword}
					className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-6 md:p-8"
				>
					<h1 className="text-xl font-bold text-white">
						Acesso protegido
					</h1>
					<p className="mt-2 text-sm text-zinc-300">
						Digite a senha para acessar esta página.
					</p>

					<input
						type="password"
						value={passwordInput}
						onChange={(event) =>
							setPasswordInput(event.target.value)
						}
						className="mt-5 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-brand-pink"
						autoFocus
					/>

					{hasError && (
						<p className="mt-3 text-xs text-red-400">
							Senha incorreta.
						</p>
					)}

					<button
						type="submit"
						className="mt-5 w-full rounded-lg bg-brand-pink py-2.5 text-sm font-bold text-white transition hover:bg-brand-pink/90"
					>
						Entrar
					</button>
				</form>
			</section>
		);
	}

	return <>{children}</>;
};

const ProtectedPropostaRoute = () => (
	<ProtectedPasswordRoute password={PROPOSTA_PASSWORD}>
		<PropostaPublica />
	</ProtectedPasswordRoute>
);

const ProtectedPropostaGeralRoute = () => (
	<ProtectedPasswordRoute password={PROPOSTA_GERAL_PASSWORD}>
		<PropostaGeralPage />
	</ProtectedPasswordRoute>
);

const ProtectedDemonstracaoDashboardRoute = () => (
	<ProtectedPasswordRoute password={DEMONSTRACAO_DASHBOARD_PASSWORD}>
		<DemonstracaoDashboardPage />
	</ProtectedPasswordRoute>
);

const ProtectedDemonstracaoLaudoRoute = () => (
	<ProtectedPasswordRoute password={DEMONSTRACAO_DASHBOARD_PASSWORD}>
		<DemonstracaoLaudoPage />
	</ProtectedPasswordRoute>
);

const ProtectedDemonstracaoDevolutivaRoute = () => (
	<ProtectedPasswordRoute password={DEMONSTRACAO_DASHBOARD_PASSWORD}>
		<DemonstracaoDevolutivaPage />
	</ProtectedPasswordRoute>
);

export const publicRoutes: Array<RouteObject> = [
	{
		path: '/',
		element: <NewPage />,
		// element: <Landing />,
		// element: <LandingCarnaval />,
	},
	{
		path: '/empresa',
		element: <BusinessLanding />,
		// element: <BusinessLandingCarnaval />,
	},
	{
		path: '/psicologo',
		element: <PsychologistLandingPage />,
	},
	{
		path: '/colaboradores',
		element: <Landing />,
		// element: <LandingCarnaval />,
	},
	{
		path: '/pacientes',
		element: <Landing />,
	},
	{
		path: '/individual',
		element: <Navigate to="/" replace />,
	},
	{
		path: '/apresentacao',
		element: <ApresentacaoPage />,
	},
	{
		path: '/demonstracao_dashboard',
		element: <ProtectedDemonstracaoDashboardRoute />,
	},
	{
		path: '/demonstracao_laudo',
		element: <ProtectedDemonstracaoLaudoRoute />,
	},
	{
		path: '/demonstracao_devolutiva',
		element: <ProtectedDemonstracaoDevolutivaRoute />,
	},
	// {
	// 	path: "beneficios",
	// 	element: <BenefitPage />
	// },
	{
		path: 'beneficios/planos',
		element: <PlanSelectionPage />,
	},
	// {
	// 	path: "beneficios/planos",
	// 	element: <PlanSelectionPage />
	// },
	{
		path: 'recuperar-senha',
		element: (
			<AuthLayout>
				<RecoverPassword />
			</AuthLayout>
		),
	},
	{
		path: 'recuperar-senha/codigo',
		element: (
			<AuthLayout>
				<RecoverCode />
			</AuthLayout>
		),
	},
	{
		path: 'recuperar-senha/recuperar',
		element: (
			<AuthLayout>
				<Recover />
			</AuthLayout>
		),
	},
	{
		path: 'oauth-callback',
		element: <OauthCallback />,
	},
	{
		path: 'agendamento',
		element: <PublicSchedulingLayout />,
		children: [
			{
				path: '',
				element: <CreatePublicSession />,
			},
		],
	},
	{
		path: 'politica-de-privacidade',
		element: <PublicSchedulingLayout />,
		children: [
			{
				path: '',
				element: <PrivacyPolicyPage />,
			},
		],
	},
	{
		path: 'termos-de-uso',
		element: <PublicSchedulingLayout />,
		children: [
			{
				path: '',
				element: <TermsOfUsePage />,
			},
		],
	},
	{
		path: 'termos-de-uso-do-profissional',
		element: <PublicSchedulingLayout />,
		children: [
			{
				path: '',
				element: <ProfessionalTermsOfUsePage />,
			},
		],
	},
	{
		path: 'termos-de-uso-do-beneficio',
		element: <PublicSchedulingLayout />,
		children: [
			{
				path: '',
				element: <BenefitTermsOfUsePage />,
			},
		],
	},
	// {
	//   path: 'gestao',
	//   element: <ManagementLayout />,
	//   children: [
	//     {
	//       path: 'exemplo',
	//       element: <CompanyAdminDashboard />,
	//     },
	//     {
	//       path: 'exemplo2',
	//       element: <AlphaEdTechDashboard />,
	//     },
	//     {
	//       path: 'colaboradores',
	//       element: <CollaboratorsPage />,
	//     },
	//     {
	//       index: true,
	//       element: <CompanyAdminDashboard />,
	//     },
	//   ],
	// },
	{
		path: '__proposta__',
		element: <ProtectedPropostaRoute />,
	},
	{
		path: 'proposta',
		element: <ProtectedPropostaGeralRoute />,
	},
	{
		path: 'sessao/acesso/:providerId',
		element: <PublicSessionAccessLayout />,
		children: [
			{
				index: true,
				element: <SessaoAcessoPublicoPage />,
			},
		],
	},
	{
		path: 'onboarding/colaborador',
		element: <CollaboratorOnboarding />,
	},
	{
		path: 'match',
		element: <MatchPage />,
	},
	{
		path: 'tutorial',
		element: <TutorialPage />,
	},
	{
		path: 'blog',
		element: <BlogListPage />,
	},
	{
		path: 'blog/:slug',
		element: <BlogPostPage />,
	},
	{
		path: '/sandbox/entrar',
		element: <SandboxEnterPage />,
	},
	{
		path: '*',
		element: <NotFound />,
	},
];
